/**
 * The sessions this browser has been in. Story VC-016.
 *
 * # Why this is local, and what that costs
 *
 * `agent_runtime_lld.md` §40 has four endpoints and none of them lists a
 * tenant's sessions. That is not an oversight in this story's path — a list
 * endpoint is a query with a retention policy, a page size and an
 * authorisation rule attached, and none of the three has been designed. So
 * "continue where you left off" is answered by what this browser remembers,
 * and the limitation is stated on the screen rather than hidden: a session
 * started on a phone does not appear on a desktop.
 *
 * # What this no longer keeps, and why that is the point
 *
 * VC-016 also kept the user's own turns here, because nothing else had
 * them: §29's `user_message_received` carries `{duplicate, message_id,
 * priority}` and not the text, so a session reconstructed from the event
 * stream alone showed the agent talking to itself. The workaround rendered
 * the remembered turns and then appended the agent's, which reads as every
 * question followed by every answer — and there was no fix on this side,
 * because a remembered turn has no sequence and no turn id to interleave by.
 *
 * RS-023 gave the runtime a transcript endpoint (§40.9) and VC-024 spends
 * it, so the turns are gone from here. `loadSessions` strips any that an
 * older version left behind and writes the stripped list back, because a
 * transcript in `localStorage` is content with no retention policy over it
 * (`AGENTS.md` §II.3.10, SEC-003) and leaving it to rot would be keeping it.
 *
 * The *title* stays, and is the one piece of content this file still holds:
 * a list of conversations needs names, and a name is not a transcript.
 *
 * No DOM and no React in this file, which is why it has a test.
 */

/** Where the list lives. Versioned, so a shape change is not a crash. */
const KEY = "voiceagent.sessions.v1";

/**
 * How many sessions are kept.
 *
 * A bound because `localStorage` is a few megabytes shared with everything
 * else on the origin, and an unbounded list of conversations is the thing
 * that fills it. The oldest go first.
 */
export const MAX_SESSIONS = 20;

/**
 * @typedef {{ id: string, kind: "call" | "chat", agent: string,
 *             startedAt: number, updatedAt: number, ended: boolean,
 *             title: string }} StoredSession
 */

/**
 * Every session this browser remembers, most recently used first.
 *
 * @param {Storage} [storage]
 * @returns {StoredSession[]}
 */
export function loadSessions(storage = defaultStorage()) {
  if (!storage) {
    return [];
  }
  let parsed;
  try {
    parsed = JSON.parse(storage.getItem(KEY) ?? "[]");
  } catch {
    // Written by a version that spelled this differently, or by something
    // else entirely. Treated as empty rather than thrown: a corrupt list is
    // a home screen with no history, and a home screen that will not render
    // is worse.
    return [];
  }
  if (!Array.isArray(parsed)) {
    return [];
  }
  const sessions = parsed
    .filter(isSession)
    .sort((left, right) => right.updatedAt - left.updatedAt);

  // A read that repairs. An older version of this file kept the user's turns
  // here; they are the runtime's now (§40.9), and forgetting them has to
  // actually remove them rather than merely stop reading them — otherwise
  // "the transcripts are gone" is true of the screen and false of the disk.
  // Written back once, on the first load after an upgrade.
  const stale = sessions.filter((session) => "turns" in session);
  const stripped = sessions.map(({ turns: _turns, ...session }) => session);
  if (stale.length > 0) {
    write(stripped, storage);
  }
  return stripped;
}

/**
 * Records a session, or updates the one with this id.
 *
 * `patch` is merged over what is there, so a caller may report one field —
 * a title, an ending — without reading the list first.
 *
 * @param {string} id
 * @param {Partial<StoredSession>} patch
 * @param {Storage} [storage]
 * @returns {StoredSession[]} the list as it now stands
 */
export function rememberSession(id, patch, storage = defaultStorage()) {
  const now = patch.updatedAt ?? Date.now();
  const existing = loadSessions(storage).find((session) => session.id === id);
  const merged = {
    id,
    kind: "chat",
    agent: "",
    title: "",
    startedAt: now,
    ended: false,
    ...existing,
    ...patch,
    updatedAt: now,
  };

  const rest = loadSessions(storage).filter((session) => session.id !== id);
  return write([merged, ...rest].slice(0, MAX_SESSIONS), storage);
}

/**
 * Names a session after the first thing said in it.
 *
 * The words, and nothing else: what was said is the runtime's to keep
 * (§40.9), and this file holds only what a *list* needs.
 *
 * @param {string} id
 * @param {string} text
 * @param {Storage} [storage]
 */
export function rememberTitle(id, text, storage = defaultStorage()) {
  const existing = loadSessions(storage).find((session) => session.id === id);
  return rememberSession(
    id,
    {
      // The first thing said is what the session is called. Later turns do
      // not rename it: a list whose entries change name as a conversation
      // goes on is a list nobody can find anything in.
      title: existing?.title || summarise(text),
    },
    storage,
  );
}

/**
 * Forgets one session.
 *
 * Local only. Ending a session server-side is §40.7's `DELETE`, and the two
 * are deliberately separate calls: removing a row from this list is not a
 * claim about the runtime, and closing a session is not a reason to lose
 * what the user typed into it.
 *
 * @param {string} id
 * @param {Storage} [storage]
 */
export function forgetSession(id, storage = defaultStorage()) {
  return write(
    loadSessions(storage).filter((session) => session.id !== id),
    storage,
  );
}

/**
 * A title from the first thing said.
 *
 * @param {string} text
 */
export function summarise(text) {
  const flat = text.replace(/\s+/g, " ").trim();
  if (flat.length <= 48) {
    return flat;
  }
  // Cut at a word rather than mid-token, so a truncated title reads as a
  // phrase and not as a hash.
  const cut = flat.slice(0, 48);
  const space = cut.lastIndexOf(" ");
  return `${space > 24 ? cut.slice(0, space) : cut}…`;
}

/**
 * "just now", "14 minutes ago", "Tuesday".
 *
 * Relative for the first day and absolute after it, because "eleven days
 * ago" is a number a reader has to convert and "2 September" is not.
 *
 * @param {number} at epoch milliseconds
 * @param {number} [now]
 */
export function describeWhen(at, now = Date.now()) {
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 45) {
    return "just now";
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  return new Date(at).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

/**
 * @param {StoredSession[]} sessions
 * @param {Storage | null} storage
 */
function write(sessions, storage) {
  if (!storage) {
    return sessions;
  }
  try {
    storage.setItem(KEY, JSON.stringify(sessions));
  } catch {
    // The quota, or a browser with storage disabled. The list this call
    // produced is still returned, so the screen shows the session that was
    // just started — it simply will not be there tomorrow. Failing the
    // navigation instead would be losing a session to bookkeeping.
  }
  return sessions;
}

/** @param {unknown} value */
function isSession(value) {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (/** @type {any} */ (value).id) === "string" &&
    typeof (/** @type {any} */ (value).updatedAt) === "number"
  );
}

/** `localStorage`, or `null` where there is none (a private window, SSR). */
function defaultStorage() {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Reading `localStorage` itself throws when the origin is denied
    // storage, which is a browser setting and not an error.
    return null;
  }
}
